import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, InputGroup, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { Check2, Plus, Trash, XLg } from 'react-bootstrap-icons';
import {
  useGetRulesEngineConfigQuery,
  useGetUserPreferencesQuery,
  useRunWarmupDeletionNowMutation,
  useUpdateRulesEngineConfigMutation,
  useUpdateUserPreferencesMutation,
} from '../services/api';
import {
  DEFAULT_RULES_ENGINE_CONFIG,
  DelayBounceSuppressionConfig,
  getHardBounceRuntimeStatus,
  HardBounceSuppressionConfig,
  RulesEngineConfig,
  RulesEngineTrigger,
  WarmupDeletionConfig,
  WarmupDeletionRuntimeState,
  WarmupDeletionRunSummary,
  WarmupIdentifierConfig,
  normalizeRulesEngineConfig,
  rulesEngineEditableSnapshot,
} from '../constants/rulesEngine';
import { preferencesTenantWriteSpread, useTenantIdForPreferences } from '../hooks/useTenantIdForPreferences';
import { useAccount } from '../contexts/AccountContext';
import EMMSectionHeader from './EMMSectionHeader';

const makeTriggerId = (): string => `trigger-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const DEFAULT_SCHEDULE_TIMEZONE = 'UTC';
const SCHEDULE_TIMEZONE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'EST/EDT (America/New_York)' },
  { value: 'America/Chicago', label: 'CST/CDT (America/Chicago)' },
  { value: 'America/Denver', label: 'MST/MDT (America/Denver)' },
  { value: 'America/Los_Angeles', label: 'PST/PDT (America/Los_Angeles)' },
  { value: 'America/Phoenix', label: 'MST (America/Phoenix)' },
  { value: 'America/Anchorage', label: 'AKST/AKDT (America/Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'HST (Pacific/Honolulu)' },
];

const isValidIanaTimezone = (timezone?: string): boolean => {
  const value = String(timezone || '').trim();
  if (!value) return false;
  try {
    Intl.DateTimeFormat('en-US', { timeZone: value }).format(new Date());
    return true;
  } catch {
    return false;
  }
};

const normalizeScheduleTimezone = (timezone?: string): string =>
  isValidIanaTimezone(timezone) ? String(timezone) : DEFAULT_SCHEDULE_TIMEZONE;

const formatDateTime = (value?: string, timezone?: string): string => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  const safeTimezone = normalizeScheduleTimezone(timezone);
  return `${parsed.toLocaleString(undefined, { timeZone: safeTimezone })} (${safeTimezone})`;
};

const formatDuration = (durationMs?: number): string => {
  const ms = Number(durationMs || 0);
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  const seconds = Math.floor(ms / 1000);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remSeconds = seconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m ${remSeconds}s`;
  if (minutes > 0) return `${minutes}m ${remSeconds}s`;
  return `${remSeconds}s`;
};

const parseIsoMs = (value?: string): number => {
  const raw = String(value || '').trim();
  if (!raw) return 0;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getSummarySortTimestamp = (summary?: WarmupDeletionRunSummary): number =>
  Math.max(parseIsoMs(summary?.finishedAt), parseIsoMs(summary?.startedAt));

const pickMostRecentRunSummary = (
  primary?: WarmupDeletionRunSummary,
  secondary?: WarmupDeletionRunSummary
): WarmupDeletionRunSummary | undefined => {
  if (!primary) return secondary;
  if (!secondary) return primary;
  return getSummarySortTimestamp(secondary) > getSummarySortTimestamp(primary) ? secondary : primary;
};

const getRuntimeSortTimestamp = (runtime?: WarmupDeletionRuntimeState): number =>
  Math.max(parseIsoMs(runtime?.lastHeartbeatAt), parseIsoMs(runtime?.startedAt));

const pickPreferredRuntime = (
  primary?: WarmupDeletionRuntimeState,
  secondary?: WarmupDeletionRuntimeState
): WarmupDeletionRuntimeState | undefined => {
  if (!primary) return secondary;
  if (!secondary) return primary;
  const primaryStatus = String(primary.status || '').toLowerCase();
  const secondaryStatus = String(secondary.status || '').toLowerCase();
  if (primaryStatus === 'running' && secondaryStatus !== 'running') return primary;
  if (secondaryStatus === 'running' && primaryStatus !== 'running') return secondary;
  return getRuntimeSortTimestamp(secondary) > getRuntimeSortTimestamp(primary) ? secondary : primary;
};

const computeNextRunDisplay = (enabled: boolean, scheduleTimeUtc: string, scheduleTimezone?: string): string => {
  if (!enabled) return 'Disabled';
  const match = String(scheduleTimeUtc || '').trim().match(/^(\d{2}):(\d{2})$/);
  if (!match) return 'Invalid schedule';
  const hh = Number(match[1]);
  const mm = Number(match[2]);
  if (!Number.isFinite(hh) || !Number.isFinite(mm) || hh < 0 || hh > 23 || mm < 0 || mm > 59) {
    return 'Invalid schedule';
  }
  const timezone = normalizeScheduleTimezone(scheduleTimezone);
  const nowMs = Date.now();
  const base = new Date(nowMs + 60_000);
  base.setSeconds(0, 0);

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    hourCycle: 'h23',
  });

  for (let offset = 0; offset <= 60 * 48; offset += 1) {
    const candidate = new Date(base.getTime() + offset * 60_000);
    const parts = formatter.formatToParts(candidate);
    const hourRaw = Number(parts.find((part) => part.type === 'hour')?.value);
    const minuteRaw = Number(parts.find((part) => part.type === 'minute')?.value);
    if (!Number.isFinite(hourRaw) || !Number.isFinite(minuteRaw)) continue;
    if (hourRaw === hh && minuteRaw === mm && candidate.getTime() > nowMs) {
      return `${candidate.toLocaleString(undefined, {
        timeZone: timezone,
      })} (${timezone})`;
    }
  }
  return 'Unable to compute next run';
};

const normalizeFolderName = (value: unknown): string =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s*:\s*/g, ':')
    .replace(/\s+/g, ' ');

const isWarmupLikeFolder = (value: unknown): boolean => /^warm ?up/.test(normalizeFolderName(value));
type WarmupPrimaryMode = 'off' | 'live';

const RulesEngine: React.FC = () => {
  // Rules Engine config and folder prefs: same tenant resolution as Sidebar / Unibox.
  const { tenantReady } = useAccount();
  const tenantIdForRulesEngine = useTenantIdForPreferences();
  const tenantIdForPrefs = tenantIdForRulesEngine;

  const { data, isLoading, error, refetch } = useGetRulesEngineConfigQuery(
    tenantIdForRulesEngine ? { tenantId: tenantIdForRulesEngine } : undefined,
    {
      refetchOnMountOrArgChange: true,
      skip: !tenantReady || !tenantIdForRulesEngine,
    }
  );
  const { data: globalRulesEngineData } = useGetRulesEngineConfigQuery(
    undefined,
    {
      skip: !tenantReady || !tenantIdForRulesEngine,
      refetchOnMountOrArgChange: true,
    }
  );
  const { data: userPreferencesData, refetch: refetchUserPreferences } = useGetUserPreferencesQuery(
    tenantIdForPrefs ? { tenantId: tenantIdForPrefs } : undefined,
    {
      skip: !tenantReady || !tenantIdForPrefs,
    }
  );
  const [runWarmupDeletionNow, { isLoading: isQueueingRunNow }] = useRunWarmupDeletionNowMutation();
  const [updateRulesEngineConfig, { isLoading: isSaving }] = useUpdateRulesEngineConfigMutation();
  const [updateUserPreferences, { isLoading: isSavingPreferences }] = useUpdateUserPreferencesMutation();

  const [draft, setDraft] = useState<RulesEngineConfig>(DEFAULT_RULES_ENGINE_CONFIG);
  const [accountStageDraft, setAccountStageDraft] = useState('');
  const [contactStageDraft, setContactStageDraft] = useState('');
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [folderSearchTerm, setFolderSearchTerm] = useState('');
  const [newFolderNameDraft, setNewFolderNameDraft] = useState('');
  const [foldersDraft, setFoldersDraft] = useState<string[]>([]);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);
  /** Cleared whenever the editable config diverges from server; set after a successful full-config save (main Save or auto-reconcile). */
  const [lastSelfSaveAtMs, setLastSelfSaveAtMs] = useState<number | null>(null);
  const autoReconcileKeyRef = useRef<string>('');

  useEffect(() => {
    if (!data) return;
    setDraft(normalizeRulesEngineConfig(data));
  }, [data]);

  const snapshotDraftJson = useMemo(
    () => JSON.stringify(rulesEngineEditableSnapshot(draft)),
    [draft]
  );
  const snapshotServerJson = useMemo(
    () => (data ? JSON.stringify(rulesEngineEditableSnapshot(data)) : ''),
    [data]
  );
  const configDirty = Boolean(data && snapshotDraftJson !== snapshotServerJson);

  useEffect(() => {
    if (configDirty) setLastSelfSaveAtMs(null);
  }, [configDirty]);

  const globalFallbackConfig = useMemo(() => {
    if (!tenantIdForRulesEngine) return null;
    if (!globalRulesEngineData) return null;
    return normalizeRulesEngineConfig(globalRulesEngineData);
  }, [globalRulesEngineData, tenantIdForRulesEngine]);

  const availableStages = useMemo(() => {
    const byKey = new Map<string, string>();
    [...draft.accountStages, ...draft.contactStages].forEach((stage) => {
      const normalized = String(stage || '').trim();
      const key = normalized.toLowerCase();
      if (!normalized || byKey.has(key)) return;
      byKey.set(key, normalized);
    });
    return Array.from(byKey.values());
  }, [draft.accountStages, draft.contactStages]);

  const tenantFoldersFromPrefs = useMemo(() => {
    const raw = (userPreferencesData as any)?.preferences?.uniboxFolders;
    if (!Array.isArray(raw)) return [] as string[];
    const byKey = new Map<string, string>();
    raw.forEach((folder: any) => {
      const value = String(folder || '').trim();
      const key = normalizeFolderName(value);
      if (!value || !key || byKey.has(key)) return;
      byKey.set(key, value);
    });
    return Array.from(byKey.values()).sort((a, b) => a.localeCompare(b));
  }, [userPreferencesData]);

  useEffect(() => {
    setFoldersDraft(tenantFoldersFromPrefs);
  }, [tenantFoldersFromPrefs]);

  const folderOptions = useMemo(() => {
    const byKey = new Map<string, string>();
    foldersDraft.forEach((folder) => {
      const value = String(folder || '').trim();
      const key = normalizeFolderName(value);
      if (!value || !key || byKey.has(key)) return;
      byKey.set(key, value);
    });
    return Array.from(byKey.values()).sort((a, b) => a.localeCompare(b));
  }, [foldersDraft]);

  const filteredFolderOptions = useMemo(() => {
    const needle = String(folderSearchTerm || '').trim().toLowerCase();
    if (!needle) return folderOptions;
    return folderOptions.filter((folder) => folder.toLowerCase().includes(needle));
  }, [folderOptions, folderSearchTerm]);

  useEffect(() => {
    const configuredTarget = String(draft.warmupDeletion?.targetFolder || '').trim();
    if (!configuredTarget) return;

    const normalizedConfiguredTarget = normalizeFolderName(configuredTarget);
    const folderSet = new Set(folderOptions.map((folder) => normalizeFolderName(folder)));
    if (folderSet.has(normalizedConfiguredTarget)) return;

    const warmupCandidates = folderOptions.filter((folder) => isWarmupLikeFolder(folder));
    if (warmupCandidates.length !== 1) return;

    const replacement = warmupCandidates[0];
    const reconcileKey = `${normalizedConfiguredTarget}->${normalizeFolderName(replacement)}`;
    if (autoReconcileKeyRef.current === reconcileKey) return;
    autoReconcileKeyRef.current = reconcileKey;

    const reconcileAsync = async () => {
      try {
        const nextConfig = normalizeRulesEngineConfig({
          ...draft,
          warmupDeletion: {
            ...draft.warmupDeletion,
            targetFolder: replacement,
          },
        });
        const saved = await updateRulesEngineConfig({
          ...(tenantIdForRulesEngine ? { tenantId: tenantIdForRulesEngine } : {}),
          config: nextConfig,
        }).unwrap();
        setDraft(normalizeRulesEngineConfig(saved));
        setLastSelfSaveAtMs(Date.now());
        setFeedback({
          type: 'success',
          message: `Target folder was updated to "${replacement}" to match your current Warm Up folder.`,
        });
      } catch (reconcileError: any) {
        setFeedback({
          type: 'danger',
          message:
            reconcileError?.data?.message ||
            reconcileError?.message ||
            'Target folder no longer exists. Please re-select a Warm Up folder.',
        });
      }
    };

    reconcileAsync();
  }, [draft, folderOptions, tenantIdForRulesEngine, updateRulesEngineConfig]);

  const setStages = (kind: 'accountStages' | 'contactStages', nextStages: string[]) => {
    const normalized = normalizeRulesEngineConfig({
      ...draft,
      [kind]: nextStages,
    });
    setDraft((prev) => ({
      ...prev,
      [kind]: normalized[kind],
      triggers: normalized.triggers,
    }));
  };

  const updateStageValue = (kind: 'accountStages' | 'contactStages', index: number, value: string) => {
    const next = [...draft[kind]];
    next[index] = value;
    setStages(kind, next);
  };

  const removeStage = (kind: 'accountStages' | 'contactStages', index: number) => {
    const next = draft[kind].filter((_, idx) => idx !== index);
    setStages(kind, next);
  };

  const addStage = (kind: 'accountStages' | 'contactStages') => {
    const value = kind === 'accountStages' ? accountStageDraft : contactStageDraft;
    const normalized = String(value || '').trim();
    if (!normalized) return;

    setStages(kind, [...draft[kind], normalized]);
    if (kind === 'accountStages') {
      setAccountStageDraft('');
    } else {
      setContactStageDraft('');
    }
  };

  const updateTrigger = (triggerId: string, patch: Partial<RulesEngineTrigger>) => {
    setDraft((prev) => ({
      ...prev,
      triggers: prev.triggers.map((trigger) => (
        trigger.id === triggerId
          ? { ...trigger, ...patch }
          : trigger
      )),
    }));
  };

  const removeTrigger = (triggerId: string) => {
    setDraft((prev) => ({
      ...prev,
      triggers: prev.triggers.filter((trigger) => trigger.id !== triggerId),
    }));
  };

  const addTrigger = () => {
    setDraft((prev) => ({
      ...prev,
      triggers: [
        ...prev.triggers,
        {
          id: makeTriggerId(),
          name: 'New Trigger',
          enabled: true,
          target: 'both',
          blockedStages: [],
        },
      ],
    }));
  };

  // Phase A (2026-05-13): patch helper for the Warm-Up Identifier card.
  // Mirrors `updateWarmupDeletion` shape so both nested config sections
  // are edited the same way. Dirty detection picks up changes
  // automatically because `warmupIdentifier` is in
  // `rulesEngineEditableSnapshot`.
  const updateWarmupIdentifier = (patch: Partial<WarmupIdentifierConfig>) => {
    setDraft((prev) => ({
      ...prev,
      warmupIdentifier: {
        ...prev.warmupIdentifier,
        ...patch,
      },
    }));
  };

  const updateWarmupDeletion = (patch: Partial<WarmupDeletionConfig>) => {
    setDraft((prev) => ({
      ...prev,
      warmupDeletion: {
        ...prev.warmupDeletion,
        ...patch,
      },
    }));
  };

  const saveDelayBounceSuppression = async (patch: Partial<DelayBounceSuppressionConfig>) => {
    // Persist this section immediately so refresh cannot silently reset the toggle.
    // Use the latest server snapshot as baseline so we do not accidentally save unrelated unsaved edits.
    const baseline = normalizeRulesEngineConfig(data || DEFAULT_RULES_ENGINE_CONFIG);
    const nextDelayBounceSuppression = normalizeRulesEngineConfig({
      delayBounceSuppression: {
        ...baseline.delayBounceSuppression,
        ...patch,
      },
    }).delayBounceSuppression;

    setDraft((prev) => ({
      ...prev,
      delayBounceSuppression: nextDelayBounceSuppression,
    }));

    try {
      const saved = await updateRulesEngineConfig({
        ...(tenantIdForRulesEngine ? { tenantId: tenantIdForRulesEngine } : {}),
        config: {
          ...baseline,
          delayBounceSuppression: nextDelayBounceSuppression,
        },
      }).unwrap();
      const normalizedSaved = normalizeRulesEngineConfig(saved);
      setDraft((prev) => ({
        ...prev,
        delayBounceSuppression: normalizedSaved.delayBounceSuppression,
        delayBounceSuppressionSummary:
          normalizedSaved.delayBounceSuppressionSummary || prev.delayBounceSuppressionSummary,
        updatedAt: normalizedSaved.updatedAt,
        updatedBy: normalizedSaved.updatedBy,
      }));
      setFeedback({ type: 'success', message: 'Soft Bounce Auto-Suppression saved.' });
    } catch (saveError: any) {
      setDraft((prev) => ({
        ...prev,
        delayBounceSuppression: baseline.delayBounceSuppression,
      }));
      setFeedback({
        type: 'danger',
        message:
          saveError?.data?.message ||
          saveError?.message ||
          'Failed to save Soft Bounce Auto-Suppression settings.',
      });
    }
  };

  const saveHardBounceSuppression = async (patch: Partial<HardBounceSuppressionConfig>) => {
    // Persist independently from soft-bounce settings. Runtime behavior remains feature-gated by backend.
    const baseline = normalizeRulesEngineConfig(data || DEFAULT_RULES_ENGINE_CONFIG);
    const nextHardBounceSuppression = normalizeRulesEngineConfig({
      hardBounceSuppression: {
        ...baseline.hardBounceSuppression,
        ...patch,
      },
    }).hardBounceSuppression;

    setDraft((prev) => ({
      ...prev,
      hardBounceSuppression: nextHardBounceSuppression,
    }));

    try {
      const saved = await updateRulesEngineConfig({
        ...(tenantIdForRulesEngine ? { tenantId: tenantIdForRulesEngine } : {}),
        config: {
          ...baseline,
          hardBounceSuppression: nextHardBounceSuppression,
        },
      }).unwrap();
      const normalizedSaved = normalizeRulesEngineConfig(saved);
      setDraft((prev) => ({
        ...prev,
        hardBounceSuppression: normalizedSaved.hardBounceSuppression,
        bounceClassification: normalizedSaved.bounceClassification,
        updatedAt: normalizedSaved.updatedAt,
        updatedBy: normalizedSaved.updatedBy,
      }));
      setFeedback({ type: 'success', message: 'Hard Bounce Suppression settings saved.' });
    } catch (saveError: any) {
      setDraft((prev) => ({
        ...prev,
        hardBounceSuppression: baseline.hardBounceSuppression,
      }));
      setFeedback({
        type: 'danger',
        message:
          saveError?.data?.message ||
          saveError?.message ||
          'Failed to save Hard Bounce Suppression settings.',
      });
    }
  };

  const saveBounceClassificationConfig = async (
    patch: Partial<RulesEngineConfig['bounceClassification']>
  ) => {
    // Activation is tenant-scoped by design; all domains/mailboxes inherit this policy.
    const baseline = normalizeRulesEngineConfig(data || DEFAULT_RULES_ENGINE_CONFIG);
    const nextTenantEnabled = patch.enabled === true
      ? true
      : patch.enabled === false
        ? false
        : Boolean(baseline.bounceClassification.enabled);
    const nextBounceClassification = normalizeRulesEngineConfig({
      bounceClassification: {
        enabled: nextTenantEnabled,
        activationScope: 'tenant',
        // Runtime truth comes from the API response after save.
        effectiveEnabled: nextTenantEnabled && Boolean(baseline.bounceClassification.effectiveEnabled),
        v1Enabled: nextTenantEnabled && Boolean(baseline.bounceClassification.effectiveEnabled),
      },
    }).bounceClassification;

    setDraft((prev) => ({
      ...prev,
      bounceClassification: nextBounceClassification,
    }));

    try {
      const saved = await updateRulesEngineConfig({
        ...(tenantIdForRulesEngine ? { tenantId: tenantIdForRulesEngine } : {}),
        config: {
          ...baseline,
          bounceClassification: nextBounceClassification,
        },
      }).unwrap();
      const normalizedSaved = normalizeRulesEngineConfig(saved);
      setDraft((prev) => ({
        ...prev,
        bounceClassification: normalizedSaved.bounceClassification,
        updatedAt: normalizedSaved.updatedAt,
        updatedBy: normalizedSaved.updatedBy,
      }));
      setFeedback({ type: 'success', message: 'Tenant-wide bounce activation policy saved.' });
    } catch (saveError: any) {
      setDraft((prev) => ({
        ...prev,
        bounceClassification: baseline.bounceClassification,
      }));
      setFeedback({
        type: 'danger',
        message:
          saveError?.data?.message ||
          saveError?.message ||
          'Failed to save tenant-wide bounce activation policy.',
      });
    }
  };

  const openFolderPicker = () => {
    setFolderSearchTerm('');
    setNewFolderNameDraft('');
    setShowFolderModal(true);
  };

  const selectTargetFolder = (folder: string) => {
    const value = String(folder || '').trim();
    if (!value) return;
    updateWarmupDeletion({ targetFolder: value });
    setShowFolderModal(false);
    setFeedback(null);
  };

  const handleCreateFolderFromModal = async () => {
    const candidate = String(newFolderNameDraft || '').trim();
    if (!candidate) return;
    const key = normalizeFolderName(candidate);
    if (!key) return;

    const existing = folderOptions.find((folder) => normalizeFolderName(folder) === key);
    if (existing) {
      selectTargetFolder(existing);
      return;
    }

    const nextFolders = [...folderOptions, candidate].sort((a, b) => a.localeCompare(b));
    try {
      await updateUserPreferences({
        ...preferencesTenantWriteSpread(tenantIdForPrefs),
        preferences: {
          uniboxFolders: nextFolders,
        },
      }).unwrap();
      setFoldersDraft(nextFolders);
      setNewFolderNameDraft('');
      setFolderSearchTerm('');
      updateWarmupDeletion({ targetFolder: candidate });
      await refetchUserPreferences();
      setShowFolderModal(false);
      setFeedback({ type: 'success', message: `Folder "${candidate}" created and selected.` });
    } catch (err: any) {
      setFeedback({
        type: 'danger',
        message: err?.data?.message || err?.message || 'Failed to create folder. Please try again.',
      });
    }
  };

  const validateWarmupFolderSelection = (): boolean => {
    if (!hasFolderOptions) {
      setFeedback({
        type: 'danger',
        message: 'No folders exist for this tenant. Click "Search folders" and add a Warm Up folder before enabling auto deletion.',
      });
      return false;
    }
    if (!targetFolderExistsInTenant) {
      setFeedback({
        type: 'danger',
        message: 'Select an existing folder before enabling auto deletion.',
      });
      return false;
    }
    if (!targetFolderIsWarmup) {
      setFeedback({
        type: 'danger',
        message: 'Selected folder must be a Warm Up folder (for example: "Warmup" or "Warm up: Team").',
      });
      return false;
    }
    return true;
  };

  const handleWarmupModeChange = (nextMode: WarmupPrimaryMode) => {
    if (nextMode === 'off') {
      updateWarmupDeletion({ enabled: false, dryRun: false });
      setFeedback(null);
      return;
    }

    if (!validateWarmupFolderSelection()) return;
    const includesSent = Boolean(warmupConfig?.includeSentByEmailKind);
    const confirmed = window.confirm(
      includesSent
        ? 'Live deletion mode will delete eligible emails from:\n\n' +
          '  1. The selected Warm Up folder.\n' +
          '  2. Verified warm-up emails associated with automated warm-up activity.\n\n' +
          'Normal business email in Inbox, Sent, and user-created folders is never touched.\n\n' +
          'Continue?'
        : 'Live deletion mode will delete eligible emails from the selected Warm Up folder.\n' +
          'Inbox, Sent, and all other folders will never be touched.\n\n' +
          'Continue?'
    );
    if (!confirmed) return;
    updateWarmupDeletion({ enabled: true, dryRun: false });
    setFeedback(null);
  };

  const handleEnableAdvancedDryRun = () => {
    if (!validateWarmupFolderSelection()) return;
    const confirmed = window.confirm(
      'Dry-run preview mode will run on schedule and only log deletion candidates.\n' +
      'No emails will be deleted while this mode is active.\n\n' +
      'Enable Dry-Run Preview?'
    );
    if (!confirmed) return;
    updateWarmupDeletion({ enabled: true, dryRun: true });
    setFeedback({
      type: 'success',
      message: 'Advanced Dry-Run Preview enabled. Scheduled runs will not delete emails.',
    });
  };

  const handlePauseDisableImmediately = async () => {
    const confirmed = window.confirm(
      'This will immediately stop all future auto-deletion runs.\nAlready deleted emails cannot be restored.\n\nContinue?'
    );
    if (!confirmed) return;
    try {
      const normalized = normalizeRulesEngineConfig({
        ...draft,
        warmupDeletion: {
          ...draft.warmupDeletion,
          enabled: false,
        },
      });
      const saved = await updateRulesEngineConfig({
        ...(tenantIdForRulesEngine ? { tenantId: tenantIdForRulesEngine } : {}),
        config: normalized,
      }).unwrap();
      setDraft(normalizeRulesEngineConfig(saved));
      setLastSelfSaveAtMs(Date.now());
      setFeedback({ type: 'success', message: 'Auto Warm-Up Deletion has been paused and disabled.' });
    } catch (saveError: any) {
      setFeedback({
        type: 'danger',
        message: saveError?.data?.message || saveError?.message || 'Failed to disable Auto Warm-Up Deletion.',
      });
    }
  };

  const warmupConfig = draft.warmupDeletion;
  const delayBounceSuppressionConfig = draft.delayBounceSuppression;
  const hardBounceSuppressionConfig = draft.hardBounceSuppression;
  const bounceClassificationConfig = draft.bounceClassification;
  const delayBounceSuppressionSummary = draft.delayBounceSuppressionSummary;
  const delayBounceRetryMode = delayBounceSuppressionConfig?.retryMode === 'never' ? 'never' : 'after_days';
  const tenantWideBounceActivationEnabled = Boolean(bounceClassificationConfig?.enabled);
  const effectiveBounceActivationEnabled = Boolean(bounceClassificationConfig?.effectiveEnabled);
  const hardBouncePolicyConfigured = Boolean(hardBounceSuppressionConfig?.enabled);
  const hardBounceRuntimeStatus = getHardBounceRuntimeStatus(
    bounceClassificationConfig,
    hardBounceSuppressionConfig
  );
  const hardBounceStatusBadgeVariant = hardBounceRuntimeStatus === 'active'
    ? 'success'
    : hardBounceRuntimeStatus === 'enabled'
      ? 'warning'
      : 'secondary';
  const hardBounceStatusBadgeLabel = hardBounceRuntimeStatus === 'active'
    ? 'Tenant Active'
    : hardBounceRuntimeStatus === 'enabled'
      ? 'Tenant Enabled'
      : 'Tenant Disabled';
  const scheduleTimezone = normalizeScheduleTimezone(String(warmupConfig?.scheduleTimezone || DEFAULT_SCHEDULE_TIMEZONE));
  const warmupLastRun = pickMostRecentRunSummary(
    draft.warmupDeletionLastRun as WarmupDeletionRunSummary | undefined,
    globalFallbackConfig?.warmupDeletionLastRun as WarmupDeletionRunSummary | undefined
  );
  const warmupRuntime = pickPreferredRuntime(
    draft.warmupDeletionRuntime as WarmupDeletionRuntimeState | undefined,
    globalFallbackConfig?.warmupDeletionRuntime as WarmupDeletionRuntimeState | undefined
  );
  const normalizedTargetFolder = normalizeFolderName(warmupConfig?.targetFolder);
  const folderSet = new Set(folderOptions.map((folder) => normalizeFolderName(folder)));
  const hasFolderOptions = folderOptions.length > 0;
  const targetFolderExistsInTenant = normalizedTargetFolder ? folderSet.has(normalizedTargetFolder) : false;
  const targetFolderIsWarmup = isWarmupLikeFolder(normalizedTargetFolder);
  const warmupConfigIsValidForRun = hasFolderOptions && targetFolderExistsInTenant && targetFolderIsWarmup;
  // Server-snapshot of warmup config (from the cached query). Re-runs whenever
  // `data` changes (initial load + after every save thanks to RulesEngine tag
  // invalidation). Used to gate the save-blocker so unrelated edits (stages,
  // triggers, bounce policies) can still be saved when the warm-up config is
  // stale (e.g. targetFolder no longer exists in user-prefs).
  const savedWarmupDeletion = useMemo(() => {
    if (!data) return null;
    return normalizeRulesEngineConfig(data).warmupDeletion;
  }, [data]);
  const warmupDeletionDirty = useMemo(() => {
    if (!savedWarmupDeletion) return false;
    return JSON.stringify(draft.warmupDeletion) !== JSON.stringify(savedWarmupDeletion);
  }, [draft.warmupDeletion, savedWarmupDeletion]);
  // Save is only blocked when the user actually changed warmup-related fields
  // AND the resulting config is invalid. Stale-but-untouched warmup config
  // surfaces via the existing in-section warning (line ~945) instead of
  // preventing saves of unrelated sections (stages, triggers, bounce policies).
  const saveBlockedByWarmupValidation = Boolean(
    warmupDeletionDirty && draft.warmupDeletion?.enabled && !warmupConfigIsValidForRun
  );
  const nextRunDisplay = computeNextRunDisplay(
    Boolean(warmupConfig?.enabled),
    String(warmupConfig?.scheduleTimeUtc || ''),
    scheduleTimezone
  );
  const warmupPrimaryMode: WarmupPrimaryMode = warmupConfig?.enabled ? 'live' : 'off';
  const warmupAdvancedDryRunActive = Boolean(warmupConfig?.enabled && warmupConfig?.dryRun);
  const scheduleTimezoneOptions = useMemo(() => {
    const byValue = new Map<string, string>();
    SCHEDULE_TIMEZONE_OPTIONS.forEach((option) => {
      byValue.set(option.value, option.label);
    });
    if (scheduleTimezone && !byValue.has(scheduleTimezone)) {
      byValue.set(scheduleTimezone, scheduleTimezone);
    }
    return Array.from(byValue.entries()).map(([value, label]) => ({ value, label }));
  }, [scheduleTimezone]);

  const topBarBtnStyle = { fontSize: '0.75rem', padding: '0.25rem 0.5rem' } as const;
  const rulesSaveShowsSpinner = isSaving;
  const rulesSaveGlowSaved = !configDirty && lastSelfSaveAtMs != null && !rulesSaveShowsSpinner;
  const rulesSaveIdleNoChanges = !configDirty && lastSelfSaveAtMs == null && !rulesSaveShowsSpinner;
  const rulesSavedAtLabel = draft.updatedAt
    ? formatDateTime(draft.updatedAt, scheduleTimezone)
    : (lastSelfSaveAtMs != null
      ? new Date(lastSelfSaveAtMs).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
      : '');

  const handleSave = async () => {
    // Only block the save if the user actually edited warmup-related fields
    // AND the new state is invalid. If warmup is stale-but-untouched, the
    // existing in-section warning surfaces it without blocking unrelated saves
    // (stages, triggers, bounce policies).
    if (warmupDeletionDirty && draft.warmupDeletion?.enabled && !warmupConfigIsValidForRun) {
      setFeedback({
        type: 'danger',
        message: hasFolderOptions
          ? 'Select an existing Warm Up folder before saving enabled auto deletion.'
          : 'No folders exist for this tenant. Create a Warm Up folder before enabling auto deletion.',
      });
      return;
    }
    try {
      const normalized = normalizeRulesEngineConfig(draft);
      const requestedTargetFolder = String(normalized.warmupDeletion?.targetFolder || '').trim();
      const saved = await updateRulesEngineConfig({
        ...(tenantIdForRulesEngine ? { tenantId: tenantIdForRulesEngine } : {}),
        config: normalized,
      }).unwrap();
      const normalizedSaved = normalizeRulesEngineConfig(saved);
      const returnedTargetFolder = String(normalizedSaved.warmupDeletion?.targetFolder || '').trim();
      // Defensive UX fallback: if API response omits targetFolder, keep the folder the user just saved.
      if (requestedTargetFolder && !returnedTargetFolder) {
        normalizedSaved.warmupDeletion = {
          ...normalizedSaved.warmupDeletion,
          targetFolder: requestedTargetFolder,
        };
      }
      setDraft(normalizedSaved);
      setLastSelfSaveAtMs(Date.now());
      setFeedback({ type: 'success', message: 'Rules Engine settings saved.' });
    } catch (saveError: any) {
      setFeedback({
        type: 'danger',
        message: saveError?.data?.message || saveError?.message || 'Failed to save Rules Engine settings.',
      });
    }
  };

  const handleRunNow = async () => {
    if (!warmupConfig?.enabled) {
      setFeedback({
        type: 'danger',
        message: 'Enable Auto Warm-Up Deletion first, then run manually.',
      });
      return;
    }
    if (!warmupConfigIsValidForRun) {
      setFeedback({
        type: 'danger',
        message: 'Select an existing Warm Up folder before running manually.',
      });
      return;
    }

    const confirmed = window.confirm(
      'Run Warm-Up Deletion now?\n\nThis queues an immediate worker run for the current tenant settings.'
    );
    if (!confirmed) return;

    try {
      await runWarmupDeletionNow(
        tenantIdForRulesEngine ? { tenantId: tenantIdForRulesEngine } : undefined
      ).unwrap();
      setFeedback({
        type: 'success',
        message: 'Manual run queued. Refresh Last Run Summary in a minute to see progress.',
      });
      setTimeout(() => {
        void refetch().catch((error: unknown) => {
          console.warn('⚠️ Rules Engine refetch after run-now skipped or failed:', error);
        });
      }, 1200);
    } catch (runNowError: any) {
      setFeedback({
        type: 'danger',
        message: runNowError?.data?.message || runNowError?.message || 'Failed to queue manual run.',
      });
    }
  };

  if (isLoading) {
    return (
      <Container fluid className="py-4 d-flex justify-content-center align-items-center" style={{ minHeight: '320px' }}>
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <>
      <EMMSectionHeader title="Rules Engine" showHelp />
      <Container
        fluid
        className="py-3"
        style={configDirty ? { paddingBottom: 'calc(4.75rem + env(safe-area-inset-bottom, 0px))' } : undefined}
      >
      {/* Action bar - same spot and size as Clients "+ Client" button */}
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <Button
            variant="outline-primary"
            size="sm"
            className="d-flex align-items-center"
            style={topBarBtnStyle}
            onClick={handleRunNow}
            disabled={isQueueingRunNow || !warmupConfig?.enabled || !warmupConfigIsValidForRun}
            title={!warmupConfig?.enabled ? 'Enable Auto Warm-Up Deletion first.' : undefined}
          >
            {isQueueingRunNow ? <Spinner size="sm" animation="border" /> : 'Run Now'}
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            className="d-flex align-items-center"
            style={topBarBtnStyle}
            onClick={() => {
              void refetch().catch((error: unknown) => {
                console.warn('⚠️ Rules Engine refetch skipped or failed:', error);
              });
            }}
          >
            Refresh
          </Button>
          {rulesSaveShowsSpinner ? (
            <Button
              variant="primary"
              size="sm"
              className="d-flex align-items-center"
              style={topBarBtnStyle}
              disabled
            >
              <Spinner size="sm" animation="border" />
            </Button>
          ) : null}
          {saveBlockedByWarmupValidation && !rulesSaveShowsSpinner ? (
            <Button
              variant="primary"
              size="sm"
              className="d-flex align-items-center"
              style={topBarBtnStyle}
              disabled
              title="Select an existing Warm Up folder before saving enabled auto deletion."
            >
              Save Rules
            </Button>
          ) : null}
          {configDirty && !rulesSaveShowsSpinner && !saveBlockedByWarmupValidation ? (
            <Button
              variant="primary"
              size="sm"
              className="d-flex align-items-center"
              style={topBarBtnStyle}
              onClick={handleSave}
            >
              Save Rules
            </Button>
          ) : null}
          {rulesSaveGlowSaved ? (
            <Button
              variant="success"
              size="sm"
              className="d-flex align-items-center"
              style={topBarBtnStyle}
              disabled
            >
              <Check2 className="me-1" aria-hidden />
              Saved
            </Button>
          ) : null}
          {rulesSaveIdleNoChanges ? (
            <Button
              variant="outline-secondary"
              size="sm"
              className="d-flex align-items-center"
              style={topBarBtnStyle}
              disabled
              title="No changes to save"
            >
              No changes
            </Button>
          ) : null}
          {rulesSaveGlowSaved && rulesSavedAtLabel ? (
            <span className="text-muted small" style={{ fontSize: '0.75rem' }}>
              {rulesSavedAtLabel}
            </span>
          ) : null}
        </div>
      </div>
      <div className="text-muted mb-3" style={{ fontSize: '0.8rem' }}>
        Manage Account Stage / Contact Stage catalogs and trigger rules that control campaign send eligibility.
      </div>

      {feedback ? (
        <Alert variant={feedback.type} className="py-2" onClose={() => setFeedback(null)} dismissible>
          {feedback.message}
        </Alert>
      ) : null}

      {error ? (
        <Alert variant="danger" className="py-2">
          Failed to load Rules Engine config:{' '}
          {(error as any)?.data?.message ||
            (error as any)?.data?.error ||
            (error as any)?.message ||
            ((error as any)?.status ? `HTTP ${(error as any).status}` : 'Unknown error')}
        </Alert>
      ) : null}

      {/*
        Phase A (2026-05-13): Warm-Up Identifier Card. Sits ABOVE the
        Auto Warm-Up Deletion card because it defines WHAT counts as
        warm-up traffic (the deletion card decides what to do with it).
        SEPARATE from Auto Filters by design — Auto Filters live on
        Unibox and continue routing inbound mail to user-created
        folders unchanged. This control is admin-managed, system-level,
        and feeds the central classifier on BOTH inbound and outbound
        writes (so outbound warm-up sends finally arrive in the
        unified table tagged `emailKind: 'warmup'`).
      */}
      <Card className="mb-3 border-info-subtle">
        <Card.Header className="py-2 fw-semibold" style={{ fontSize: '0.85rem' }}>
          Warm-Up Identifier (System Filter)
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="mb-1" style={{ fontSize: '0.8rem' }}>
                  Warm-Up Identifier Token
                </Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  value={draft.warmupIdentifier?.pattern || ''}
                  onChange={(event) => updateWarmupIdentifier({ pattern: event.target.value })}
                  placeholder="e.g. 6HY2M4R"
                  maxLength={256}
                />
                <div className="text-muted mt-1" style={{ fontSize: '0.78rem' }}>
                  Subject or Body containing this token (case-insensitive, whole-word match) will be
                  classified as warm-up traffic and shown in the protected{' '}
                  <strong>Warm Up</strong> system folder. Applies to both inbound and outbound mail.
                </div>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label className="mb-1" style={{ fontSize: '0.8rem' }}>
                  Status
                </Form.Label>
                <Form.Check
                  type="switch"
                  id="warmup-identifier-enabled-switch"
                  label={draft.warmupIdentifier?.enabled
                    ? 'Enabled — classifying matching mail as warm-up'
                    : 'Disabled — no system warm-up classification'}
                  checked={Boolean(draft.warmupIdentifier?.enabled)}
                  onChange={(event) => updateWarmupIdentifier({ enabled: event.target.checked })}
                  style={{ fontSize: '0.8rem' }}
                />
                <div className="text-muted mt-2" style={{ fontSize: '0.78rem' }}>
                  Independent from Auto Filters (Unibox sidebar). Existing user-created warm-up
                  Auto Filters continue working unchanged. This control adds a system-managed
                  classification layer that surfaces warm-up sends in the protected{' '}
                  <strong>Warm Up</strong> folder.
                </div>
              </Form.Group>
            </Col>
          </Row>

          {draft.warmupIdentifier?.enabled
            && (!draft.warmupIdentifier?.pattern || !String(draft.warmupIdentifier.pattern).trim())
            ? (
              <Alert variant="warning" className="py-2 mt-3 mb-0" style={{ fontSize: '0.8rem' }}>
                Identifier is enabled but the token is empty. Enter the warm-up vendor token
                (e.g. <code>6HY2M4R</code>) before saving — an empty token matches nothing.
              </Alert>
            )
            : null}
        </Card.Body>
      </Card>

      <Card className="mb-3 border-warning-subtle">
        <Card.Header className="py-2 fw-semibold" style={{ fontSize: '0.85rem' }}>
          Auto Warm-Up Deletion (Infrastructure Hygiene)
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="mb-1" style={{ fontSize: '0.8rem' }}>Deletion Mode</Form.Label>
                <Form.Select
                  size="sm"
                  value={warmupPrimaryMode}
                  onChange={(event) => handleWarmupModeChange(event.target.value as WarmupPrimaryMode)}
                  disabled={!hasFolderOptions}
                >
                  <option value="off">Off (No scheduled cleanup)</option>
                  <option value="live">Live Deletion (Scheduled)</option>
                </Form.Select>
              </Form.Group>
              <div className="text-muted mt-1" style={{ fontSize: '0.8rem' }}>
                {warmupAdvancedDryRunActive
                  ? 'Dry-Run Preview override is active. Scheduled runs only log candidates and do not delete emails.'
                  : warmupPrimaryMode === 'live'
                  ? 'Live Deletion is active. Eligible warm-up emails will be deleted on schedule.'
                  : 'Cleanup is off. No scheduled warm-up deletions will run.'}
              </div>

              <div className="mt-3 d-flex align-items-center gap-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => (
                    warmupAdvancedDryRunActive
                      ? updateWarmupDeletion({ enabled: true, dryRun: false })
                      : handleEnableAdvancedDryRun()
                  )}
                >
                  {warmupAdvancedDryRunActive ? 'Disable Dry-Run Preview' : 'Enable Dry-Run Preview (Advanced)'}
                </Button>
                {warmupAdvancedDryRunActive ? <Badge bg="warning" text="dark">Dry-Run Active</Badge> : null}
              </div>
              <div className="text-muted mt-1" style={{ fontSize: '0.8rem' }}>
                Dry-Run Preview is optional and for safe verification only. While active, scheduled runs log candidates and do not delete emails.
              </div>

              <div className="mt-3">
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={handlePauseDisableImmediately}
                >
                  Pause &amp; Disable Immediately
                </Button>
              </div>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="mb-1" style={{ fontSize: '0.8rem' }}>Target Folder</Form.Label>
                <InputGroup size="sm">
                  <Form.Control
                    readOnly
                    role="button"
                    value={warmupConfig?.targetFolder || ''}
                    onClick={openFolderPicker}
                    placeholder="Search folders"
                  />
                  <Button variant="outline-secondary" onClick={openFolderPicker}>
                    Search folders
                  </Button>
                </InputGroup>
                {!hasFolderOptions ? (
                  <div className="text-danger fw-semibold mt-1" style={{ fontSize: '0.78rem' }}>
                    No folders exist for this tenant. Click "Search folders" to open the picker and add one.
                  </div>
                ) : !targetFolderExistsInTenant ? (
                  <div className="text-danger fw-semibold mt-1" style={{ fontSize: '0.78rem' }}>
                    Select an existing folder from the picker.
                  </div>
                ) : !targetFolderIsWarmup ? (
                  <div className="text-danger fw-semibold mt-1" style={{ fontSize: '0.78rem' }}>
                    The selected folder must be a Warm Up folder name (for example: "Warmup" or "Warm up: Team").
                  </div>
                ) : null}
                {warmupConfig?.includeSentByEmailKind ? (
                  <div className="text-danger fw-semibold mt-1" style={{ fontSize: '0.78rem' }}>
                    Auto deletion only applies to (1) emails inside the selected Warm Up folder, and (2) verified warm-up emails associated with automated warm-up activity. Normal business email in Inbox, Sent, and user-created folders is never touched.
                  </div>
                ) : (
                  <div className="text-danger fw-semibold mt-1" style={{ fontSize: '0.78rem' }}>
                    Auto deletion will only run inside this folder. Inbox, Sent, and all other folders will never be touched.
                  </div>
                )}
              </Form.Group>

              <Row className="g-2">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="mb-1" style={{ fontSize: '0.8rem' }}>Run Schedule (Daily)</Form.Label>
                    <Form.Control
                      size="sm"
                      type="time"
                      value={String(warmupConfig?.scheduleTimeUtc || '03:30')}
                      onChange={(event) => updateWarmupDeletion({ scheduleTimeUtc: event.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="mb-1" style={{ fontSize: '0.8rem' }}>Timezone</Form.Label>
                    <Form.Select
                      size="sm"
                      value={scheduleTimezone}
                      onChange={(event) => updateWarmupDeletion({ scheduleTimezone: event.target.value })}
                    >
                      {scheduleTimezoneOptions.map((timezoneOption) => (
                        <option key={timezoneOption.value} value={timezoneOption.value}>
                          {timezoneOption.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <div className="text-muted mt-1" style={{ fontSize: '0.78rem' }}>
                Recommended: off-peak hours (for example 03:00–05:00 in {scheduleTimezone}) to protect IMAP performance.
              </div>
              <div className="text-muted mt-1" style={{ fontSize: '0.76rem' }}>
                Execution guardrails: max {warmupConfig?.maxConcurrentMailboxes ?? 5} concurrent cleanup sessions, max {warmupConfig?.maxMailboxesPerMinute ?? 10} mailboxes/minute.
              </div>
            </Col>
          </Row>

          <Row className="g-3 mt-1">
            <Col md={6}>
              <Card bg="light" className="h-100">
                <Card.Body className="py-2">
                  <div className="fw-semibold mb-2" style={{ fontSize: '0.82rem' }}>Current Retention Rules</div>
                  <Table size="sm" bordered className="mb-0" style={{ fontSize: '0.75rem' }}>
                    <thead>
                      <tr>
                        <th>Mailbox Age</th>
                        <th>Retention</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>0–{warmupConfig?.mailboxAgeThresholdDays ?? 30} days</td>
                        <td>{warmupConfig?.retentionDaysForNewMailbox ?? 30} days</td>
                      </tr>
                      <tr>
                        <td>{(warmupConfig?.mailboxAgeThresholdDays ?? 30) + 1}+ days</td>
                        <td>{warmupConfig?.retentionDaysForMatureMailbox ?? 14} days</td>
                      </tr>
                    </tbody>
                  </Table>
                  <div className="text-muted mt-1" style={{ fontSize: '0.74rem' }}>
                    Retention is based on mailbox creation date and recalculated daily.
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card bg="light" className="h-100">
                <Card.Body className="py-2">
                  <div className="fw-semibold mb-2" style={{ fontSize: '0.82rem' }}>Last Run Summary</div>
                  <div style={{ fontSize: '0.76rem' }}>
                    <div><strong>Status:</strong> {warmupRuntime ? 'Running' : (warmupLastRun?.status || 'Never run')}</div>
                    <div><strong>Started:</strong> {warmupRuntime ? formatDateTime(warmupRuntime.startedAt, scheduleTimezone) : formatDateTime(warmupLastRun?.startedAt, scheduleTimezone)}</div>
                    <div><strong>Finished:</strong> {warmupRuntime ? 'In progress' : formatDateTime(warmupLastRun?.finishedAt, scheduleTimezone)}</div>
                    <div><strong>Mailboxes Processed:</strong> {warmupRuntime?.processedMailboxes ?? warmupLastRun?.processedMailboxes ?? 0} / {warmupRuntime?.totalMailboxes ?? warmupLastRun?.totalMailboxes ?? 0}</div>
                    <div><strong>Emails Deleted:</strong> {warmupRuntime?.deletedEmails ?? warmupLastRun?.deletedEmails ?? 0}</div>
                    <div><strong>Candidates:</strong> {warmupRuntime?.candidateEmails ?? warmupLastRun?.candidateEmails ?? 0}</div>
                    <div><strong>Errors:</strong> {warmupRuntime?.errors ?? warmupLastRun?.errors ?? 0}</div>
                    <div><strong>Duration:</strong> {warmupRuntime ? 'In progress' : formatDuration(warmupLastRun?.durationMs)}</div>
                    <div><strong>Next Scheduled Run:</strong> {nextRunDisplay}</div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {warmupConfig?.includeSentByEmailKind ? (
            <Alert variant="warning" className="mb-0 mt-3 py-2" style={{ fontSize: '0.78rem' }}>
              <strong>Safety scope:</strong> This rule only applies to:
              {' '}<strong>(1)</strong> emails inside the selected Warm Up folder, and
              {' '}<strong>(2)</strong> verified warm-up emails associated with automated warm-up activity.
              {' '}Normal business email in Inbox, Sent, and user-created folders is never modified.
              <br />
              Use "Search folders" to choose an existing folder or create one if none exists.
            </Alert>
          ) : (
            <Alert variant="warning" className="mb-0 mt-3 py-2" style={{ fontSize: '0.78rem' }}>
              <strong>Safety scope:</strong> This auto-deletion rule only applies to emails inside the selected Warm-Up folder.
              Inbox, Sent, and user-created folders are never modified by this rule.
              <br />
              Use "Search folders" to choose an existing folder or create one if none exists.
            </Alert>
          )}
        </Card.Body>
      </Card>

      <Row className="g-3 mb-3">
        <Col md={6}>
          <Card className="h-100 border-info-subtle">
            <Card.Header className="py-2 fw-semibold" style={{ fontSize: '0.85rem' }}>
              Soft Bounce / Delay Bounce Auto-Suppression
            </Card.Header>
            <Card.Body>
              <div className="text-muted mb-2" style={{ fontSize: '0.76rem' }}>
                Applies only to temporary delivery failures such as Delivery Status Notification (Delay), greylisting,
                throttling, mailbox full, or temporary receiving-server issues.
              </div>
              <Form.Check
                type="switch"
                id="rules-delay-bounce-auto-suppress"
                label="Enable soft-bounce auto-suppression"
                checked={Boolean(delayBounceSuppressionConfig?.enabled)}
                onChange={(event) => {
                  void saveDelayBounceSuppression({ enabled: event.target.checked });
                }}
              />
              <Form.Group className="mt-3" style={{ maxWidth: '320px' }}>
                <Form.Label className="mb-1" style={{ fontSize: '0.8rem' }}>Retry behavior</Form.Label>
                <Form.Select
                  size="sm"
                  value={delayBounceRetryMode}
                  onChange={(event) => {
                    const nextMode = event.target.value === 'never' ? 'never' : 'after_days';
                    void saveDelayBounceSuppression({
                      retryMode: nextMode,
                      retryAfterDays: nextMode === 'never' ? 0 : 7,
                    });
                  }}
                >
                  <option value="after_days">Then retry after 7 days</option>
                  <option value="never">Do not retry at all</option>
                </Form.Select>
              </Form.Group>
              <div className="text-muted mt-2" style={{ fontSize: '0.78rem' }}>
                Policy: auto-suppress when a recipient has
                {' '}
                {delayBounceSuppressionConfig?.suppressAfterDelayBounces24h ?? 2}
                {' '}
                delay bounces in 24h
                {' '}
                <strong>or</strong>
                {' '}
                {delayBounceSuppressionConfig?.suppressAfterDelayBounces48h ?? 3}
                {' '}
                delay bounces in 48h,
                {' '}
                {delayBounceRetryMode === 'never' ? 'do not retry at all.' : 'then retry after 7 days.'}
              </div>
              <div className="text-muted mt-2" style={{ fontSize: '0.78rem' }}>
                Last auto-suppressed:
                {' '}
                <strong>
                  {delayBounceSuppressionSummary?.lastSuppressedAt
                    ? formatDateTime(delayBounceSuppressionSummary.lastSuppressedAt, scheduleTimezone)
                    : 'Never'}
                </strong>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="h-100 border-danger-subtle">
            <Card.Header className="py-2 fw-semibold d-flex justify-content-between align-items-center" style={{ fontSize: '0.85rem' }}>
              <span>Hard Bounce Suppression</span>
              <Badge bg={hardBounceStatusBadgeVariant}>{hardBounceStatusBadgeLabel}</Badge>
            </Card.Header>
            <Card.Body>
              <div className="text-muted mb-2" style={{ fontSize: '0.76rem' }}>
                <div>
                  Applies to permanent delivery failures such as invalid mailbox, user unknown, or domain not found.
                </div>
                <div className="mt-1">
                  When runtime is active, hard-bounced recipients are automatically suppressed and added to the Block
                  List to prevent future sends.
                </div>
                <div className="mt-1">
                  The switches below save tenant configuration intent. The status badge above shows runtime truth.
                </div>
              </div>
              <Form.Check
                type="switch"
                id="rules-bounce-classification-tenant"
                className="mb-2"
                label="Enable tenant-wide soft/hard bounce framework"
                checked={tenantWideBounceActivationEnabled}
                onChange={(event) => {
                  void saveBounceClassificationConfig({ enabled: event.target.checked });
                }}
              />
              <div className="text-muted mb-2" style={{ fontSize: '0.76rem' }}>
                Configuration intent only. Activation scope is tenant-wide, and all current and future tenant domains/mailboxes inherit it automatically.
              </div>
              <Form.Check
                type="switch"
                id="rules-hard-bounce-suppress"
                label="Enable hard-bounce suppression policy"
                checked={Boolean(hardBounceSuppressionConfig?.enabled)}
                onChange={(event) => {
                  void saveHardBounceSuppression({ enabled: event.target.checked });
                }}
              />
              <div className="text-muted mb-2" style={{ fontSize: '0.76rem' }}>
                Configuration intent only. Runtime auto-block requires tenant active status and this policy to remain enabled.
              </div>
              <Form.Group className="mt-3" style={{ maxWidth: '320px' }}>
                <Form.Label className="mb-1" style={{ fontSize: '0.8rem' }}>Suppression action</Form.Label>
                <Form.Select
                  size="sm"
                  value={String(hardBounceSuppressionConfig?.suppressAfterHardBounces ?? 1)}
                  onChange={(event) => {
                    const suppressAfterHardBounces = Number(event.target.value) === 2 ? 2 : 1;
                    void saveHardBounceSuppression({ suppressAfterHardBounces });
                  }}
                >
                  <option value="1">Suppress immediately after 1 hard bounce</option>
                  <option value="2">Suppress after 2 hard bounces</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mt-3" style={{ maxWidth: '320px' }}>
                <Form.Label className="mb-1" style={{ fontSize: '0.8rem' }}>Suppression scope</Form.Label>
                <Form.Select
                  size="sm"
                  value={hardBounceSuppressionConfig?.scope === 'mailbox' ? 'mailbox' : 'global'}
                  onChange={(event) => {
                    const scope = event.target.value === 'mailbox' ? 'mailbox' : 'global';
                    void saveHardBounceSuppression({ scope });
                  }}
                >
                  <option value="global">Block globally across all campaigns/mailboxes</option>
                  <option value="mailbox">Block for this mailbox only</option>
                </Form.Select>
              </Form.Group>
              <div className="text-muted mt-2" style={{ fontSize: '0.78rem' }}>
                Runtime status:{' '}
                {hardBounceRuntimeStatus === 'active'
                  ? 'Hard-bounce auto-block is live for this tenant.'
                  : tenantWideBounceActivationEnabled && !hardBouncePolicyConfigured
                    ? 'Tenant is enabled, but hard-bounce suppression policy is turned off, so auto-block is not live.'
                    : tenantWideBounceActivationEnabled && !effectiveBounceActivationEnabled
                      ? 'Tenant is enabled, but runtime is not active yet, so hard bounces will not auto-block.'
                      : 'Tenant is disabled, so hard bounces will not auto-block.'}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col md={6}>
          <Card>
            <Card.Header className="py-2 fw-semibold" style={{ fontSize: '0.85rem' }}>
              Account Stages
            </Card.Header>
            <Card.Body>
              {draft.accountStages.map((stage, index) => (
                <InputGroup key={`account-stage-${index}`} size="sm" className="mb-2">
                  <Form.Control
                    value={stage}
                    onChange={(event) => updateStageValue('accountStages', index, event.target.value)}
                    placeholder="Account stage"
                  />
                  <Button
                    variant="outline-danger"
                    onClick={() => removeStage('accountStages', index)}
                    title="Delete stage"
                  >
                    <Trash size={12} />
                  </Button>
                </InputGroup>
              ))}
              <InputGroup size="sm">
                <Form.Control
                  value={accountStageDraft}
                  onChange={(event) => setAccountStageDraft(event.target.value)}
                  placeholder="Add account stage"
                />
                <Button variant="outline-primary" onClick={() => addStage('accountStages')}>
                  <Plus size={12} className="me-1" />
                  Add
                </Button>
              </InputGroup>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card>
            <Card.Header className="py-2 fw-semibold" style={{ fontSize: '0.85rem' }}>
              Contact Stages
            </Card.Header>
            <Card.Body>
              {draft.contactStages.map((stage, index) => (
                <InputGroup key={`contact-stage-${index}`} size="sm" className="mb-2">
                  <Form.Control
                    value={stage}
                    onChange={(event) => updateStageValue('contactStages', index, event.target.value)}
                    placeholder="Contact stage"
                  />
                  <Button
                    variant="outline-danger"
                    onClick={() => removeStage('contactStages', index)}
                    title="Delete stage"
                  >
                    <Trash size={12} />
                  </Button>
                </InputGroup>
              ))}
              <InputGroup size="sm">
                <Form.Control
                  value={contactStageDraft}
                  onChange={(event) => setContactStageDraft(event.target.value)}
                  placeholder="Add contact stage"
                />
                <Button variant="outline-primary" onClick={() => addStage('contactStages')}>
                  <Plus size={12} className="me-1" />
                  Add
                </Button>
              </InputGroup>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center py-2">
          <span className="fw-semibold" style={{ fontSize: '0.85rem' }}>Triggers</span>
          <Button variant="outline-primary" size="sm" onClick={addTrigger}>
            <Plus size={12} className="me-1" />
            New Trigger
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          <Table responsive className="mb-0" style={{ fontSize: '0.75rem' }}>
            <thead className="table-light">
              <tr>
                <th style={{ width: '90px' }}>Enabled</th>
                <th style={{ minWidth: '230px' }}>Trigger Name</th>
                <th style={{ width: '150px' }}>Target</th>
                <th>Blocked Stages</th>
                <th style={{ width: '70px' }} />
              </tr>
            </thead>
            <tbody>
              {draft.triggers.map((trigger) => (
                <tr key={trigger.id}>
                  <td>
                    <Form.Check
                      type="switch"
                      checked={trigger.enabled}
                      onChange={(event) => updateTrigger(trigger.id, { enabled: event.target.checked })}
                    />
                  </td>
                  <td>
                    <Form.Control
                      size="sm"
                      value={trigger.name}
                      onChange={(event) => updateTrigger(trigger.id, { name: event.target.value })}
                    />
                  </td>
                  <td>
                    <Form.Select
                      size="sm"
                      value={trigger.target}
                      onChange={(event) => updateTrigger(trigger.id, { target: event.target.value as RulesEngineTrigger['target'] })}
                    >
                      <option value="account">Account</option>
                      <option value="contact">Contact</option>
                      <option value="both">Both</option>
                    </Form.Select>
                  </td>
                  <td>
                    <div className="text-muted mb-1" style={{ fontSize: '0.72rem' }}>
                      Toggle stages to block. Remove a chip to unblock.
                    </div>
                    <div
                      style={{
                        border: '1px solid #dee2e6',
                        borderRadius: 4,
                        padding: '0.5rem 0.6rem',
                        maxHeight: 120,
                        overflowY: 'auto',
                        background: '#fff',
                      }}
                    >
                      {availableStages.map((stage) => {
                        const checked = trigger.blockedStages.includes(stage);
                        return (
                          <Form.Check
                            key={`${trigger.id}-blocked-${stage}`}
                            type="checkbox"
                            id={`${trigger.id}-blocked-${stage}`}
                            checked={checked}
                            onChange={(event) => {
                              const nextChecked = event.target.checked;
                              updateTrigger(trigger.id, {
                                blockedStages: nextChecked
                                  ? Array.from(new Set([...trigger.blockedStages, stage]))
                                  : trigger.blockedStages.filter((value) => value !== stage),
                              });
                            }}
                            label={stage}
                            style={{ marginBottom: 4 }}
                          />
                        );
                      })}
                      {availableStages.length === 0 ? (
                        <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                          No stages available
                        </div>
                      ) : null}
                    </div>
                    <div className="mt-1 d-flex flex-wrap gap-1">
                      {trigger.blockedStages.length === 0 ? (
                        <span className="text-muted" style={{ fontSize: '0.72rem' }}>
                          No blocked stages
                        </span>
                      ) : (
                        trigger.blockedStages.map((stage) => (
                          <Badge
                            key={`${trigger.id}-badge-${stage}`}
                            bg="secondary"
                            className="d-inline-flex align-items-center"
                            style={{ gap: 6, paddingRight: 6 }}
                          >
                            <span>{stage}</span>
                            <button
                              type="button"
                              onClick={() => {
                                updateTrigger(trigger.id, {
                                  blockedStages: trigger.blockedStages.filter((value) => value !== stage),
                                });
                              }}
                              aria-label={`Unblock ${stage}`}
                              title="Unblock"
                              style={{
                                border: 'none',
                                background: 'transparent',
                                color: 'inherit',
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: 0,
                                cursor: 'pointer',
                                opacity: 0.85,
                              }}
                            >
                              <XLg size={10} />
                            </button>
                          </Badge>
                        ))
                      )}
                    </div>
                  </td>
                  <td className="text-end">
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => removeTrigger(trigger.id)}
                      title="Delete trigger"
                    >
                      <Trash size={12} />
                    </Button>
                  </td>
                </tr>
              ))}
              {draft.triggers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center text-muted py-3">
                    No triggers configured yet.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={showFolderModal} onHide={() => setShowFolderModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem' }}>Select Target Folder</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="mb-1" style={{ fontSize: '0.8rem' }}>Search folders</Form.Label>
            <Form.Control
              size="sm"
              type="search"
              value={folderSearchTerm}
              onChange={(event) => setFolderSearchTerm(event.target.value)}
              placeholder="Type folder name..."
            />
          </Form.Group>

          <div className="border rounded" style={{ maxHeight: '220px', overflowY: 'auto' }}>
            {filteredFolderOptions.length ? (
              filteredFolderOptions.map((folder) => {
                const isSelected = normalizeFolderName(folder) === normalizedTargetFolder;
                return (
                  <button
                    key={folder}
                    type="button"
                    className={`btn btn-sm w-100 text-start rounded-0 border-0 ${isSelected ? 'btn-primary' : 'btn-light'}`}
                    onClick={() => selectTargetFolder(folder)}
                  >
                    {folder}
                  </button>
                );
              })
            ) : (
              <div className="p-3 text-muted" style={{ fontSize: '0.8rem' }}>
                No folders found. Create one below.
              </div>
            )}
          </div>

          <Form.Group className="mt-3">
            <Form.Label className="mb-1" style={{ fontSize: '0.8rem' }}>Create folder</Form.Label>
            <InputGroup size="sm">
              <Form.Control
                value={newFolderNameDraft}
                onChange={(event) => setNewFolderNameDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleCreateFolderFromModal();
                  }
                }}
                placeholder='Example: Warmup or Warm up: Team'
              />
              <Button
                variant="primary"
                onClick={handleCreateFolderFromModal}
                disabled={isSavingPreferences || !String(newFolderNameDraft || '').trim()}
              >
                {isSavingPreferences ? <Spinner size="sm" animation="border" /> : 'Add Folder'}
              </Button>
            </InputGroup>
            <div className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>
              Create folders here if none exist yet, then select the Warm Up folder.
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => setShowFolderModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
    {configDirty && !showFolderModal ? (
      <div
        className="border-top bg-body shadow-sm d-flex align-items-center justify-content-between flex-wrap gap-2 px-3 py-2"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1020,
          paddingBottom: 'max(0.35rem, env(safe-area-inset-bottom))',
        }}
        role="region"
        aria-label="Unsaved Rules Engine changes"
      >
        <span className="text-muted small fw-medium">You have unsaved Rules Engine changes</span>
        <div className="d-flex align-items-center gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => {
              void refetch().catch((error: unknown) => {
                console.warn('⚠️ Rules Engine refetch skipped or failed:', error);
              });
            }}
          >
            Refresh
          </Button>
          {isSaving ? (
            <Button variant="primary" size="sm" disabled>
              <Spinner size="sm" animation="border" />
            </Button>
          ) : (
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              disabled={saveBlockedByWarmupValidation}
              title={saveBlockedByWarmupValidation ? 'Select an existing Warm Up folder before saving enabled auto deletion.' : undefined}
            >
              Save Rules
            </Button>
          )}
        </div>
      </div>
    ) : null}
    </>
  );
};

export default RulesEngine;
